import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, Button, Space, message } from "antd";
import { FilePdfOutlined, DownloadOutlined } from "@ant-design/icons";
import { ReportDefinition } from "../../services/reportRegistry";
import { reportService } from "../../services/reportService";
import { frontDeskService } from "../../services/adminService";
import { financeService } from "../../services/financeService";
import { formatLkr } from "../../utils/currency";
import ReportFilterPanel from "./ReportFilters";
import { REPORT_COLUMNS } from "./reportColumns";
import { REPORT_SUMMARIES } from "./reportSummary";

interface ReportViewerProps {
  report: ReportDefinition;
}

function exportToCSV(filename: string, columns: any[], rows: Record<string, any>[]) {
  const headers = columns.map((c: any) => c.title || c.key);
  const dataKeys = columns.map((c: any) => c.dataIndex || c.key);
  const csvLines = [
    headers.join(","),
    ...rows.map((row) =>
      dataKeys
        .map((key) => {
          const val = row[key] ?? "";
          return `"${String(val).replace(/"/g, '""')}"`;
        })
        .join(","),
    ),
  ];
  const blob = new Blob([csvLines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * The finance endpoints return structured objects (totals alongside rows) so
 * the dashboard can be rendered properly. The generic report table needs a
 * flat row array, so reshape each one here — the money columns line up with
 * the column definitions in reportColumns.tsx.
 */
function flattenFinanceReport(reportId: string, raw: any): Record<string, any>[] {
  if (!raw || typeof raw !== "object") return [];

  switch (reportId) {
    case "finance-profit-loss": {
      const sections: Array<[string, any[]]> = [
        ["Income", raw.income],
        ["Cost of sales", raw.cost_of_sales],
        ["Operating expense", raw.operating_expenses],
      ];
      return sections.flatMap(([section, rows]) =>
        (Array.isArray(rows) ? rows : []).map((r: any) => ({ section, ...r })),
      );
    }
    case "finance-day-book":
      return (raw.rows || []).map((r: any) => ({
        ...r,
        debit: r.side === "debit" ? r.amount : 0,
        credit: r.side === "credit" ? r.amount : 0,
      }));
    case "finance-trial-balance":
    case "finance-cash-book":
    case "finance-daily-cash":
    case "finance-daily-card":
    case "finance-daily-bank":
      return raw.rows || [];
    case "finance-reconciliation": {
      const revenue = raw.revenue || {};
      const fo = revenue.front_office || {};
      const pos = revenue.restaurant_pos || {};
      const laundry = revenue.laundry || {};
      const collections = raw.collections || {};
      const foCash = collections.front_office || {};
      const posCash = collections.restaurant_pos || {};
      const laundryCash = collections.laundry || {};
      const queue = raw.pending_postings || { pending: 0, failed: 0 };
      return [
        ...(revenue.sources || []).map((s: any) => ({ section: "Revenue — source", ...s })),
        { section: "Revenue — ledger", label: "General ledger income", amount: revenue.gl_income },
        {
          section: "Revenue — variance",
          label: "Source minus ledger (all source systems)",
          amount: revenue.variance,
        },
        {
          section: "Front office",
          label: `Folio charges vs front-office journals (cutover ${fo.cutover_date || "—"})`,
          count: fo.journals,
          amount: fo.variance,
        },
        {
          section: "Restaurant",
          label: `Guest folio + walk-in POS vs their journals (cutover ${pos.cutover_date || "—"})`,
          count: pos.journals,
          amount: pos.variance,
        },
        {
          section: "Laundry",
          label: `Guest folio + walk-in laundry vs their journals (cutover ${laundry.cutover_date || "—"})`,
          count: laundry.journals,
          amount: laundry.variance,
        },
        {
          section: "Collections",
          label: "Payments received",
          amount: collections.source_total,
          count: collections.source_count,
        },
        {
          section: "Collections — ledger",
          label: "Cash / bank / card movement",
          amount: collections.gl_cash_movement,
        },
        {
          section: "Collections — front office",
          label: "Folio receipts vs front-office cash movement",
          amount: foCash.variance,
        },
        {
          section: "Collections — restaurant",
          label: "Walk-in tenders vs restaurant cash movement",
          amount: posCash.variance,
        },
        {
          section: "Collections — laundry",
          label: "Walk-in receipts vs laundry cash movement",
          amount: laundryCash.variance,
        },
        ...(raw.inventory
          ? [
              {
                section: "Inventory — purchases",
                label: "Purchase invoices received vs their journals (1200 + 1040)",
                count: raw.inventory.purchases.journals,
                amount: raw.inventory.purchases.variance,
              },
              {
                section: "Inventory — stock",
                label: "Stock movements vs inventory journals (1200)",
                count: raw.inventory.stock.journals,
                amount: raw.inventory.stock.variance,
              },
              {
                section: "Inventory — supplier payments",
                label: "Supplier settlements vs cash / bank / card movement",
                count: raw.inventory.supplier_payments.source_count,
                amount: raw.inventory.supplier_payments.variance,
              },
            ]
          : []),
        {
          section: "Posting queue",
          label: `${queue.pending} deferred, ${queue.failed} failed posting(s)`,
          count: queue.pending + queue.failed,
          amount: null,
        },
      ];
    }
    case "finance-room-performance": {
      const k = raw.kpis || {};
      const rate = (v: any, suffix = "") =>
        v === null || v === undefined ? "n/a" : `${v}${suffix}`;
      const lkr = (v: any) =>
        v === null || v === undefined ? "n/a" : formatLkr(v);
      return [
        { room_number: "Occupancy", room_type: rate(k.occupancy_pct, "%") },
        { room_number: "ADR", room_type: lkr(k.adr) },
        { room_number: "RevPAR", room_type: lkr(k.revpar) },
        { room_number: "Rooms sold", room_type: String(k.rooms_sold ?? 0) },
        {
          room_number: "Rooms available",
          room_type: k.rooms_available === null || k.rooms_available === undefined
            ? "n/a"
            : String(k.rooms_available),
        },
        {
          room_number: "Window",
          room_type: k.clamped_from_cutover
            ? `clamped to ledger start ${k.from}`
            : `${k.from} – ${k.to}`,
        },
        ...(raw.rooms || []).map((r: any) => ({ ...r })),
      ];
    }
    case "finance-restaurant-performance": {
      const k = raw.kpis || {};
      const rate = (v: any, suffix = "") =>
        v === null || v === undefined ? "n/a" : `${v}${suffix}`;
      const lkr = (v: any) =>
        v === null || v === undefined ? "n/a" : formatLkr(v);
      const control = raw.control;
      return [
        { label: "Net revenue", detail: lkr(k.net_revenue) },
        { label: "Output VAT", detail: lkr(k.vat) },
        { label: "Average ticket", detail: lkr(k.avg_ticket) },
        { label: "Walk-in share", detail: rate(k.walk_in_share_pct, "%") },
        { label: "Walk-in orders", detail: String(k.walk_in_orders ?? 0) },
        { label: "Orders", detail: String(k.orders ?? 0) },
        {
          label: "Window",
          detail: k.clamped_from_cutover
            ? `clamped to ledger start ${k.from}`
            : `${k.from} – ${k.to}`,
        },
        ...(raw.channels || []).map((c: any) => ({
          label: c.label,
          detail: `${c.walk_in_orders} of ${c.orders} walk-in`,
          orders: c.orders,
          revenue: c.net_revenue,
        })),
        // The control is a verdict, not a total: it gets its own row so the
        // summary cannot add a variance to the revenue it is measuring.
        ...(control
          ? [
              {
                label: "Control: source vs ledger",
                detail: `${control.matched ? "matched" : "VARIANCE"} (${formatLkr(control.variance)})`,
              },
            ]
          : []),
      ];
    }
    case "finance-stock-valuation": {
      const control = raw.control;
      return [
        ...(raw.items || []).map((i: any) => ({ ...i })),
        // The control is a verdict, not an item: it carries no quantity or
        // value so it can never be counted twice by the totals row.
        {
          code: "",
          name: control
            ? `Control: stock ledger vs GL 1200 — ${control.matched ? "matched" : "VARIANCE"} (${formatLkr(control.variance)})`
            : "",
          value: null,
        },
      ];
    }
    case "finance-food-cost": {
      const k = raw;
      const pct = (v: any) => (v === null || v === undefined ? "n/a" : `${v}%`);
      const control = k.control;
      return [
        { label: "Food cost (5100)", detail: "ingredients issued from the store", amount: k.food_cost },
        { label: "Beverage cost (5200)", detail: "drink issued from the store", amount: k.beverage_cost },
        { label: "Restaurant revenue", detail: "the denominator", amount: k.restaurant_revenue },
        { label: "Food Cost %", detail: pct(k.food_cost_pct), amount: null },
        {
          label: "Recipe coverage of ordered quantity",
          detail: `${pct(k.recipe_coverage_pct)} — how much of the cost above the system could know`,
          amount: null,
        },
        {
          label: "Configured / ordered quantity",
          detail: `${k.configured_quantity} of ${k.ordered_quantity}`,
          amount: null,
        },
        { label: "Laundry materials (5300)", detail: "", amount: k.laundry_materials },
        { label: "Other direct costs (5400)", detail: "", amount: k.other_direct_costs },
        {
          label: "Control: issues & write-offs vs ledger cost",
          detail: control ? `${control.matched ? "matched" : "VARIANCE"} (${formatLkr(control.variance)})` : "n/a",
          amount: control ? control.variance : null,
        },
        {
          label: "Window",
          detail: k.clamped_from_cutover
            ? `clamped to reporting start ${k.reporting_start}`
            : `${k.from} – ${k.to}`,
          amount: null,
        },
      ];
    }
    case "finance-purchase-register":
    case "finance-payroll-register":
      return raw.rows || [];
    case "finance-ap-aging":
    case "finance-ar-aging": {
      const c = raw.control;
      if (!c) return raw.rows || [];
      const isAp = reportId === "finance-ap-aging";
      const detail = isAp
        ? `listed ${formatLkr(c.aged_total)} vs GL 2010 ${formatLkr(c.gl_balance)}`
        : `listed ${formatLkr(c.source_total)} — never posted before cutover ${formatLkr(
            c.pre_cutover,
          )} — ledger debt with no debtor ${formatLkr(c.unattributed)}`;
      return [
        ...(raw.rows || []),
        // The control is a verdict, not a debt. It carries no amount so the
        // totals row cannot add a variance to the debt it is measuring —
        // the same reason the stock valuation ends with a text row.
        {
          reference: "",
          party: `Control: ${detail} — ${c.matched ? "matched" : "VARIANCE"}`,
          business_date: null,
          due_date: null,
          days_overdue: null,
          bucket: "",
          amount: null,
        },
      ];
    }
    case "finance-annual-pnl": {
      const r = raw;
      const months: any[] = r.months || [];
      const yoy = r.vs_previous_year;
      const change =
        yoy && yoy.income_change_pct !== null
          ? `${yoy.income_change_pct > 0 ? "+" : ""}${yoy.income_change_pct.toFixed(1)}%`
          : null;
      const verdict = yoy
        ? `vs ${r.previous_year.year} ${r.previous_year.income === 0 ? "" : `income ${change}`}` +
          `${yoy.partial ? " (partial year — not comparable)" : ""}`
        : "no comparable prior year";

      return [
        ...months.map((m) => ({
          ...m,
          // The distinction the report exists to keep visible: a month the
          // ledger never covered has no figure at all, while one it covered
          // but never posted is a truthful zero.
          note: m.covered
            ? m.income === 0 && m.expense === 0
              ? "no postings"
              : ""
            : "outside ledger history",
        })),
        // Everything below the line is a fact about the year, not a line in
        // it. All four figures are null so `GenericSummary` cannot add a
        // difference, a coverage count or a growth rate to the totals above.
        {
          label: "",
          note:
            `Ledger ${r.ledger?.start || "—"} to ${r.ledger?.end || "—"} · ` +
            `${r.months_covered} of 12 months covered · ${verdict}`,
          income: null,
          expense: null,
          profit: null,
          margin: null,
        },
      ];
    }
    default:
      return [];
  }
}

const ReportViewer: React.FC<ReportViewerProps> = ({ report }) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [staffList, setStaffList] = useState<Array<{ id: string; full_name: string }>>([]);
  const [roomList, setRoomList] = useState<Array<{ id: string; room_number: string }>>([]);
  const [departmentList, setDepartmentList] = useState<Array<{ id: string; code: string; name: string }>>([]);
  const [bookingList, setBookingList] = useState<
    Array<{ id: string; guest_name?: string; confirmation_number?: string; room_number?: string }>
  >([]);
  const [folioMeta, setFolioMeta] = useState<{ folio?: any; summary?: any } | null>(null);

  useEffect(() => {
    if (!report.filters.includes("bookingId")) return;
    let cancelled = false;
    const loadBookings = async () => {
      try {
        const res: any = await frontDeskService.getBookings({ limit: 500 });
        if (!cancelled) setBookingList(Array.isArray(res?.data) ? res.data : []);
      } catch {
        if (!cancelled) setBookingList([]);
      }
    };
    loadBookings();
    return () => {
      cancelled = true;
    };
  }, [report.filters]);

  useEffect(() => {
    const loadMeta = async () => {
      try {
        const [staffRes, roomRes] = await Promise.all([
          reportService.getStaffList(),
          reportService.getRoomList(),
        ]);
        setStaffList(Array.isArray((staffRes as any).data) ? (staffRes as any).data : []);
        setRoomList(Array.isArray((roomRes as any).data) ? (roomRes as any).data : []);
      } catch {
        // ignore
      }
    };
    loadMeta();
  }, []);

  useEffect(() => {
    if (!report.filters.includes("department")) return;
    let cancelled = false;
    financeService
      .getDepartments()
      .then((rows) => {
        if (!cancelled) setDepartmentList(rows);
      })
      .catch(() => {
        if (!cancelled) setDepartmentList([]);
      });
    return () => {
      cancelled = true;
    };
  }, [report.filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reportService.getReport(report.endpoint, filters);
      const raw = (res as any).data;

      // Guest Folio returns { entries, summary, folio } — flatten to entries for table
      if (report.id === "guest-folio" && raw && !Array.isArray(raw)) {
        setData(raw.entries || []);
        setFolioMeta({ folio: raw.folio, summary: raw.summary });
      } else if (report.category === "finance") {
        setData(flattenFinanceReport(report.id, raw));
        setFolioMeta(null);
      } else {
        setData(Array.isArray(raw) ? raw : []);
        setFolioMeta(null);
      }
    } catch {
      message.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  }, [report.endpoint, report.id, report.category, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportPDF = async () => {
    if (!report.pdfEndpoint) {
      message.info("PDF export not available for this report");
      return;
    }
    try {
      const blob = await reportService.getReportPdf(report.pdfEndpoint, filters);
      reportService.downloadPdf(blob, `${report.id}-report.pdf`);
    } catch {
      message.error("Failed to export PDF");
    }
  };

  const handleExportCSV = () => {
    const cols = REPORT_COLUMNS[report.id] || report.columns;
    exportToCSV(report.id, cols as any[], data);
  };

  const handleReset = () => {
    setFilters({});
  };

  // Use per-report columns, fall back to registry columns
  const columns = REPORT_COLUMNS[report.id] || report.columns;

  // Use per-report summary, fall back to none
  const renderSummary = REPORT_SUMMARIES[report.id];

  return (
    <div>
      <ReportFilterPanel
        report={report}
        filters={filters}
        onChange={setFilters}
        onApply={fetchData}
        onReset={handleReset}
        staffList={staffList}
        roomList={roomList}
        departmentList={departmentList}
        bookingList={bookingList}
      />

      <Card
        title={report.title}
        extra={
          <Space>
            {report.pdfEndpoint && (
              <Button icon={<FilePdfOutlined />} onClick={handleExportPDF}>
                Export PDF
              </Button>
            )}
            <Button icon={<DownloadOutlined />} onClick={handleExportCSV}>
              Export CSV
            </Button>
          </Space>
        }
      >
        {folioMeta?.summary && (
          <div style={{ marginBottom: 12, display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
            {(() => {
              const booking = bookingList.find((b) => b.id === filters.bookingId);
              if (!booking) return null;
              return (
                <span>
                  <strong>{booking.guest_name || "Unknown"}</strong>
                  {booking.confirmation_number ? ` · ${booking.confirmation_number}` : ""}
                  {booking.room_number ? ` · Room ${booking.room_number}` : ""}
                </span>
              );
            })()}
            <span>
              Charges: LKR{" "}
              {Number(folioMeta.summary.total_charges || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
            <span>
              Payments: LKR{" "}
              {Number(folioMeta.summary.total_payments || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
            <span style={{ fontWeight: 600 }}>
              Balance: LKR{" "}
              {Number(folioMeta.summary.balance || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
              })}
            </span>
          </div>
        )}
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(_r, i) => String(i)}
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (total) => `${total} records` }}
          scroll={{ x: 1200 }}
          size="small"
          summary={() => {
            if (!renderSummary || data.length === 0) return null;
            return renderSummary(data, columns) as React.ReactElement;
          }}
        />
      </Card>
    </div>
  );
};

export default ReportViewer;
